import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { personaApiURL } from './apiRoutes';
import { DropdownData } from '../models/dropdownData';
import { SortObject } from '../models/sortObject';
import sortHelper from 'src/app/helpers/sort';
import { LoadingService } from './shared/loading.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileDropdownsService {
  constructor(public http: HttpClient, public loadingService: LoadingService) {}
  private user_id: string;

  generateApiUrl(section, subsection, subtype, type, isField: boolean, personaSubsectionIdentifier, personaIdentifier) {
    let url = isField
      ? this.generateFieldApi(section, subsection, subtype, type, personaSubsectionIdentifier, personaIdentifier)
      : this.generateTypeApi(section, subsection, subtype, type);
    return url;
  }

  generateTypeApi(section: string, subsection: string, subtype: string, type: string) {
    let url = personaApiURL + '/sections/' + section + '/subsections/' + subsection + '/' + subtype + '/' + type;
    return subtype == 'extras' ? url + '/types' : url;
  }

  generateFieldApi(
    section: string,
    subsection: string,
    subtype: string,
    type: string,
    personaSubsectionIdentifier: string,
    personaIdentifier: string
  ) {
    let url = '';
    return (url =
      personaApiURL +
      '/' +
      personaIdentifier +
      '/sections/' +
      section +
      '/subsections/' +
      subsection +
      '/' +
      personaSubsectionIdentifier +
      '/' +
      subtype +
      '/' +
      type);
  }

  /* dropdown */
  async getDropdownValues(
    section: string,
    subsection: string,
    subtype: string,
    type: string,
    isField: boolean,
    personaSubsectionIdentifier?: string,
    personaIdentifier?: string,
    sortObject?: SortObject[]
  ): Promise<any> {
    let url = this.generateApiUrl(
      section,
      subsection,
      subtype,
      type,
      isField,
      personaSubsectionIdentifier,
      personaIdentifier
    );
    if (sortObject) {
      url += sortHelper.constructSort(sortObject);
    }

    return this.http.get(url).toPromise();
  }
  createValue(
    section: string,
    subsection: string,
    subtype: string,
    type: string,
    isField: boolean,
    data: any,
    personaSubsectionIdentifier?: string,
    personaIdentifier?: string
  ) {
    let url = this.generateApiUrl(
      section,
      subsection,
      subtype,
      type,
      isField,
      personaSubsectionIdentifier,
      personaIdentifier
    );
    // if file upload remove identifier from request body
    let isBadgeUpload = section == 'certification' && subsection == 'achievement' && subtype == 'lov' && type == 'type';
    if ((type == 'file' && isField) || isBadgeUpload) {
      var request = JSON.parse(data.get('request'));
      let id = request.identifier;
      delete request['identifier'];
      data.delete('request');
      data.append('request', JSON.stringify(request));
      // if doing update add it to url
      if (id) {
        url += '/' + id;
      }
     return this.http.post(url, data, { reportProgress: true, observe: 'events' });
    } else {
      return this.http.post(url, data, { reportProgress: true, observe: 'events' });
    }
  }

  async updateValue(
    section: string,
    subsection: string,
    subtype: string,
    type: string,
    isField: boolean,
    data: any,
    personaSubsectionIdentifier?: string,
    personaIdentifier?: string,
    extraFileId?: string
  ): Promise<any> {
    let url = await this.generateApiUrl(
      section,
      subsection,
      subtype,
      type,
      isField,
      personaSubsectionIdentifier,
      personaIdentifier
    );
    var id = extraFileId ? extraFileId : data.identifier;
    return this.http.put(url + '/' + id, data).toPromise();
  }

  async removeValue(
    section: string,
    subsection: string,
    subtype: string,
    type: string,
    isField: boolean,
    id: string,
    personaSubsectionIdentifier?: string,
    personaIdentifier?: string
  ): Promise<any> {
    let url = await this.generateApiUrl(
      section,
      subsection,
      subtype,
      type,
      isField,
      personaSubsectionIdentifier,
      personaIdentifier
    );
    return this.http.put(url + '/' + id+ '/status', { status: "DELETED" }).toPromise();
  }
  async downloadFile(
    section: string,
    subsection: string,
    subtype: string,
    extraTypeIdentifier: string,
    personaSubsectionIdentifier?: string,
    personaIdentifier?: string
  ) {
    let url = this.generateFieldApi(
      section,
      subsection,
      subtype,
      extraTypeIdentifier,
      personaSubsectionIdentifier,
      personaIdentifier
    );
    url += '/file';
    this.loadingService.loadingProgress = 0;
    return this.http
      .get(url, { responseType: 'blob' as 'json', reportProgress: true, observe: 'events' })
      .subscribe((response: any) => {
        this.loadingService.fileLoading = true;
        let loadingNum = Math.round((response.loaded / response.total) * 100);
        if (loadingNum > 0 && loadingNum < 101) {
          this.loadingService.loadingProgress = loadingNum;
        }

        if (response.type === HttpEventType.Response) {
          /* Get filename from Content-Disposition header */
          var filename = '';
          var disposition = response.headers.get('Content-Disposition');
          if (disposition && disposition.indexOf('attachment') !== -1) {
            var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            var matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
          }
          // This does the trick
          var a = document.createElement('a');
          a.href = window.URL.createObjectURL(response.body);
          a.download = filename;
          a.dispatchEvent(new MouseEvent('click'));
          this.loadingService.fileLoading = false;
          this.loadingService.loadingProgress = 0;
        }
      });
  }
  /*  dropdown end */

  async extraFieldsCounter(
    section: string,
    subsection: string,
    context: string,
    personaSubsectionIdentifier?: string,
    personaIdentifier?: string
  ) {
    let url = this.generateFieldApi(
      section,
      subsection,
      'extras',
      context,
      personaSubsectionIdentifier,
      personaIdentifier
    );
    url += '/count';
    return this.http.get(url).toPromise();
  }
  /* responsible persons */
  getAllResponsiblePersons(section: string, subsection: string, type: string): Promise<DropdownData[]> {
    var responsiblePersons: string =
      personaApiURL + '/sections/' + section + '/subsections/' + subsection + '/lov/' + type + '/responsible';
    return this.http.get<DropdownData[]>(responsiblePersons).toPromise();
  }
  /* responsible persons end */

    /* responsible persons */
    getAllManagers(section: string, subsection: string, type: string): Promise<DropdownData[]> {
      var manager: string =
        personaApiURL + '/sections/' + section + '/subsections/' + subsection + '/lov/' + type + '/manager';
      return this.http.get<DropdownData[]>(manager).toPromise();
    }
    /* responsible persons end */
}
